import { useState, useEffect, lazy, Suspense } from 'react';
import { supabase, Article } from './lib/supabase';
import { LogOut, Settings, Key } from 'lucide-react';

const Auth = lazy(() => import('./components/Auth'));
const ArticleList = lazy(() => import('./components/ArticleList'));
const ArticleEditor = lazy(() => import('./components/ArticleEditor'));
const ArticleView = lazy(() => import('./components/ArticleView'));
const PublicArticleView = lazy(() => import('./components/PublicArticleView'));
const PublicPageView = lazy(() => import('./components/PublicPageView'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const ChangePassword = lazy(() => import('./components/ChangePassword'));
const SocialLinksManager = lazy(() => import('./components/SocialLinksManager'));
const PagesManager = lazy(() => import('./components/PagesManager'));
const FooterManager = lazy(() => import('./components/FooterManager'));
const HeaderManager = lazy(() => import('./components/HeaderManager'));
const WidgetsManager = lazy(() => import('./components/WidgetsManager'));
const CategoriesManager = lazy(() => import('./components/CategoriesManager'));
const FileManager = lazy(() => import('./components/FileManager'));
const SEOFilesManager = lazy(() => import('./components/SEOFilesManager'));

const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
  </div>
);

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [viewingArticle, setViewingArticle] = useState<Article | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentAdminSection, setCurrentAdminSection] = useState<'articles' | 'social' | 'pages' | 'footer' | 'header' | 'widgets' | 'categories' | 'files' | 'seo'>('articles');
  const [hasMainPage, setHasMainPage] = useState(false);
  const [currentPageSlug, setCurrentPageSlug] = useState<string | null>(null);

  useEffect(() => {
    checkUser();
    checkMainPage();
    checkCurrentPage();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        (() => {
          setUser(session?.user ?? null);
          setLoading(false);
        })();
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  async function checkUser() {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    setLoading(false);
  }

  async function checkMainPage() {
    const { data } = await supabase
      .from('pages')
      .select('id')
      .eq('page_type', 'main')
      .eq('is_active', true)
      .maybeSingle();

    setHasMainPage(!!data);
  }

  async function checkCurrentPage() {
    const currentPath = window.location.pathname;
    const cleanPath = currentPath.replace(/^\/|\/$/g, '');

    if (!cleanPath || cleanPath === 'privacy-policy') {
      return;
    }

    const { data } = await supabase
      .from('pages')
      .select('slug')
      .eq('slug', cleanPath)
      .eq('is_active', true)
      .maybeSingle();

    if (data) {
      setCurrentPageSlug(data.slug);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setUser(null);
    setAdminMode(false);
  }

  function handleEditArticle(article: Article) {
    setSelectedArticle(article);
    setShowEditor(true);
  }

  function handleCreateNew() {
    setSelectedArticle(null);
    setShowEditor(true);
  }

  function handleViewArticle(article: Article) {
    setViewingArticle(article);
  }

  function handleCloseEditor() {
    setShowEditor(false);
    setSelectedArticle(null);
  }

  function handleCloseView() {
    setViewingArticle(null);
  }

  function handleSave() {
    setShowEditor(false);
    setSelectedArticle(null);
    window.location.reload();
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!user && adminMode) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Auth onAuthSuccess={() => checkUser()} />
      </Suspense>
    );
  }

  if (adminMode && user) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <div className="fixed top-4 right-4 z-40 flex gap-2">
          <button
            onClick={() => {
              setAdminMode(false);
              checkMainPage();
              checkCurrentPage();
            }}
            className="flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg shadow-lg transition-colors"
          >
            Публичный вид
          </button>
          <button
            onClick={() => setShowChangePassword(true)}
            className="flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg shadow-lg transition-colors"
          >
            <Key size={18} />
            Сменить пароль
          </button>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-700 px-4 py-2 rounded-lg shadow-lg transition-colors"
          >
            <LogOut size={18} />
            Выйти
          </button>
        </div>

        <div className="min-h-screen bg-gray-100 pt-20">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6 flex gap-4 border-b border-gray-300 pb-2">
              <button
                onClick={() => setCurrentAdminSection('articles')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'articles'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Статьи
              </button>
              <button
                onClick={() => setCurrentAdminSection('pages')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'pages'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Страницы
              </button>
              <button
                onClick={() => setCurrentAdminSection('header')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'header'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Шапка
              </button>
              <button
                onClick={() => setCurrentAdminSection('social')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'social'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Соц. сети
              </button>
              <button
                onClick={() => setCurrentAdminSection('footer')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'footer'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Подвал
              </button>
              <button
                onClick={() => setCurrentAdminSection('widgets')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'widgets'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Виджеты
              </button>
              <button
                onClick={() => setCurrentAdminSection('categories')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'categories'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Категории
              </button>
              <button
                onClick={() => setCurrentAdminSection('files')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'files'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Файлы
              </button>
              <button
                onClick={() => setCurrentAdminSection('seo')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  currentAdminSection === 'seo'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                SEO
              </button>
            </div>

            {currentAdminSection === 'articles' && (
              <ArticleList
                onEditArticle={handleEditArticle}
                onCreateNew={handleCreateNew}
                onViewArticle={handleViewArticle}
              />
            )}

            {currentAdminSection === 'pages' && <PagesManager />}

            {currentAdminSection === 'header' && <HeaderManager />}

            {currentAdminSection === 'social' && <SocialLinksManager />}

            {currentAdminSection === 'footer' && <FooterManager />}

            {currentAdminSection === 'widgets' && <WidgetsManager />}

            {currentAdminSection === 'categories' && <CategoriesManager />}

            {currentAdminSection === 'files' && <FileManager />}

            {currentAdminSection === 'seo' && <SEOFilesManager />}
          </div>
        </div>

        {showEditor && (
          <ArticleEditor
            article={selectedArticle}
            onClose={handleCloseEditor}
            onSave={handleSave}
          />
        )}

        {viewingArticle && (
          <ArticleView
            article={viewingArticle}
            onClose={handleCloseView}
          />
        )}

        {showChangePassword && (
          <ChangePassword onClose={() => setShowChangePassword(false)} />
        )}
      </Suspense>
    );
  }

  const currentPath = window.location.pathname;

  if (currentPath === '/privacy-policy') {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <button
          onClick={() => setAdminMode(true)}
          className="fixed bottom-4 right-4 z-40 p-3 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg transition-colors"
          title={user ? "Режим администратора" : "Вход в систему"}
        >
          <Settings size={24} />
        </button>
        <PrivacyPolicyPage />
      </Suspense>
    );
  }

  if (currentPath === '/' && hasMainPage) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <button
          onClick={() => setAdminMode(true)}
          className="fixed bottom-4 right-4 z-40 p-3 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg transition-colors"
          title={user ? "Режим администратора" : "Вход в систему"}
        >
          <Settings size={24} />
        </button>
        <PublicPageView pageSlug="main" />
      </Suspense>
    );
  }

  if (currentPageSlug) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <button
          onClick={() => setAdminMode(true)}
          className="fixed bottom-4 right-4 z-40 p-3 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg transition-colors"
          title={user ? "Режим администратора" : "Вход в систему"}
        >
          <Settings size={24} />
        </button>
        <PublicPageView pageSlug={currentPageSlug} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <button
        onClick={() => setAdminMode(true)}
        className="fixed bottom-4 right-4 z-40 p-3 bg-blue-900 hover:bg-blue-800 text-white rounded-full shadow-lg transition-colors"
        title={user ? "Режим администратора" : "Вход в систему"}
      >
        <Settings size={24} />
      </button>
      <PublicArticleView />
    </Suspense>
  );
}

export default App;
